import { components } from "@/api-schema";
import { CasaviFilterElement, CasaviFilterType } from "@/api-schema-enums";
import i18next from "i18next";
import { z } from "zod";

type CasaviContactWhitelist = components["schemas"]["CasaviContactWhitelist"];
export type { CasaviContactWhitelist };

/**
 * Validates if a regex pattern is valid
 */
export function validateRegexPattern(value?: string): boolean {
  if (!value || value.trim() === "") {
    return true; // Empty values are allowed
  }
  try {
    new RegExp(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Shared Zod schema for casavi contact whitelist forms
 */
export const CasaviContactWhitelistFormSchema = z
  .object({
    element: z.enum(CasaviFilterElement),
    type: z.enum(CasaviFilterType),
    caseSensitive: z.boolean(),
    value: z.string().min(1, i18next.t("casavi:ValueIsRequired")),
  })
  .refine(
    (data) => {
      if (data.type === CasaviFilterType.Regex) {
        return validateRegexPattern(data.value);
      }
      return true;
    },
    {
      message: i18next.t("common:InvalidRegexPattern"),
      path: ["value"],
    },
  );

export type CasaviContactWhitelistFormSchemaType = z.infer<typeof CasaviContactWhitelistFormSchema>;

/**
 * Validates casavi contact whitelist for duplicates (for create form)
 */
export function validateCasaviContactWhitelistDuplicate(
  contactWhitelists: CasaviContactWhitelist[] | undefined,
  values?: CasaviContactWhitelistFormSchemaType,
): { hasErrors: boolean; errors: string[] } {
  if (!contactWhitelists || !Array.isArray(contactWhitelists) || !values) {
    return { hasErrors: false, errors: [] };
  }

  const errors: string[] = [];

  const duplicate = contactWhitelists.find(
    (item: CasaviContactWhitelist) =>
      item.element === values.element &&
      item.type === values.type &&
      item.caseSensitive === values.caseSensitive &&
      item.value === values.value,
  );

  if (duplicate) {
    errors.push(i18next.t("casavi:ContactWhitelistEntryWithValuesExists"));
  }

  return { hasErrors: errors.length > 0, errors };
}

/**
 * Validates casavi contact whitelist for duplicates (for update form, excluding current entry)
 */
export function validateCasaviContactWhitelistUpdateDuplicate(
  contactWhitelists: CasaviContactWhitelist[] | undefined,
  currentUuid: string | undefined,
  values?: CasaviContactWhitelistFormSchemaType,
): { hasErrors: boolean; errors: string[] } {
  if (!contactWhitelists || !Array.isArray(contactWhitelists) || !values) {
    return { hasErrors: false, errors: [] };
  }

  const errors: string[] = [];

  const duplicate = contactWhitelists.find(
    (item: CasaviContactWhitelist) =>
      item.uuid !== currentUuid &&
      item.element === values.element &&
      item.type === values.type &&
      item.caseSensitive === values.caseSensitive &&
      item.value === values.value,
  );

  if (duplicate) {
    errors.push(i18next.t("casavi:ContactWhitelistOtherEntryWithValuesExists"));
  }

  return { hasErrors: errors.length > 0, errors };
}
